import React, { useMemo, useState } from "react";
import "./ResourcesDashboard.scss";

const unique = (items, key) =>
  [...new Set(items.map((item) => item[key]).filter(Boolean))].sort();

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Not scheduled";
const dayMs = 86400000;

export default function ResourcesDashboard({ data, loading, error }) {
  const [tab, setTab] = useState("reviewers");
  const [filters, setFilters] = useState({
    engagement: "",
    inspection: "",
    role: "",
    status: "",
    search: "",
  });
  const [cycle, setCycle] = useState("");
  const [period, setPeriod] = useState("week");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState([]);
  const reviewers = data?.reviewers || [];
  const roster =
    tab === "unassigned"
      ? reviewers.filter((item) => item.status === "Available")
      : reviewers;
  const cycles = data?.cycles || [];
  const activeCycle = cycle || String(cycles[0]?.id || "");
  const baseDate = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const days = period === "week" ? 10 : 28;
  const slots = Array.from(
    { length: days },
    (_, index) => new Date(baseDate.getTime() + (offset + index) * dayMs)
  );
  const visible = roster.filter((person) => {
    if (filters.engagement && person.engagement !== filters.engagement)
      return false;
    if (filters.inspection && person.inspection !== filters.inspection)
      return false;
    if (filters.role && person.role !== filters.role) return false;
    if (filters.status && person.status !== filters.status) return false;
    const query = filters.search.trim().toLowerCase();
    return (
      !query ||
      [person.name, person.skills, person.role, person.engagement].some(
        (value) =>
          String(value || "")
            .toLowerCase()
            .includes(query)
      )
    );
  });

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setSelected([]);
  };
  const allSelected =
    visible.length > 0 &&
    visible.every((person) => selected.includes(person.id));
  const metricRows = data
    ? [
        ["bx-group", data.metrics.total, "Total Reviewers", "blue"],
        ["bx-check-circle", data.metrics.allocated, "Allocated", "green"],
        ["bx-user", data.metrics.available, "Available", "slate"],
        [
          "bx-error-circle",
          data.metrics.overAllocated,
          "Over-allocated",
          "red",
        ],
        ["bx-star", data.metrics.specialistSkills, "Specialist Skills", "blue"],
      ]
    : [];

  if (loading)
    return (
      <div className="resources-dashboard">
        <p className="resources-empty">Loading resource data…</p>
      </div>
    );
  if (error)
    return (
      <div className="resources-dashboard">
        <p className="resources-empty" role="alert">
          {error}
        </p>
      </div>
    );
  if (!data)
    return (
      <div className="resources-dashboard">
        <p className="resources-empty">No resource data is available.</p>
      </div>
    );

  return (
    <div className="resources-dashboard">
      <header className="resources-dashboard__header">
        <div>
          <h1 className="piers-page-title">Resources</h1>
          <p>
            Overview of resource allocation and team assignments across your
            inspection cycle.
          </p>
        </div>
        <div>
          <span>
            {new Date(data.asOf).toLocaleDateString("en-US", {
              weekday: "long",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
          <select
            aria-label="Inspection cycle"
            value={activeCycle}
            onChange={(event) => setCycle(event.target.value)}
          >
            {cycles.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      </header>
      <section className="resources-metrics" aria-label="Resources summary">
        {metricRows.map(([icon, value, label, tone]) => (
          <article
            key={label}
            className={`resources-metric resources-metric--${tone}`}
          >
            <i className={`bx ${icon}`} aria-hidden="true" />
            <div>
              <strong>{value}</strong>
              <span>{label}</span>
              <small>Current firm data</small>
            </div>
          </article>
        ))}
      </section>
      <section className="resources-filters" aria-label="Filter resources">
        <label>
          Inspection Cycle
          <select
            value={activeCycle}
            onChange={(event) => setCycle(event.target.value)}
          >
            {cycles.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Engagement
          <select
            value={filters.engagement}
            onChange={(event) => setFilter("engagement", event.target.value)}
          >
            <option value="">All Engagements</option>
            {unique(reviewers, "engagement").map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Inspection
          <select
            value={filters.inspection}
            onChange={(event) => setFilter("inspection", event.target.value)}
          >
            <option value="">All Inspections</option>
            {unique(reviewers, "inspection").map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Role
          <select
            value={filters.role}
            onChange={(event) => setFilter("role", event.target.value)}
          >
            <option value="">All Roles</option>
            {unique(reviewers, "role").map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select
            value={filters.status}
            onChange={(event) => setFilter("status", event.target.value)}
          >
            <option value="">All</option>
            {unique(reviewers, "status").map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className="resources-filters__search">
          <span className="visually-hidden">Search resources</span>
          <i className="bx bx-search" />
          <input
            value={filters.search}
            onChange={(event) => setFilter("search", event.target.value)}
            placeholder="Search name, skill, or firm..."
          />
        </label>
      </section>
      <div className="resources-main">
        <section className="resources-panel resources-roster">
          <div className="resources-roster__tabs">
            <button
              type="button"
              className={tab === "reviewers" ? "is-active" : ""}
              onClick={() => {
                setTab("reviewers");
                setSelected([]);
              }}
            >
              Reviewers ({reviewers.length})
            </button>
            <button
              type="button"
              className={tab === "unassigned" ? "is-active" : ""}
              onClick={() => {
                setTab("unassigned");
                setSelected([]);
              }}
            >
              Unassigned (
              {reviewers.filter((item) => item.status === "Available").length})
            </button>
          </div>
          <div className="resources-roster__scroll">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all visible reviewers"
                      checked={allSelected}
                      onChange={(event) =>
                        setSelected(
                          event.target.checked
                            ? visible.map((person) => person.id)
                            : []
                        )
                      }
                    />
                  </th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Primary Skills</th>
                  <th>Current Load</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((person) => (
                  <tr key={person.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${person.name}`}
                        checked={selected.includes(person.id)}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, person.id]
                              : current.filter((id) => id !== person.id)
                          )
                        }
                      />
                    </td>
                    <td>
                      <span className="resources-person">
                        <i>{person.initials}</i>
                        <b>{person.name}</b>
                      </span>
                    </td>
                    <td>{person.role}</td>
                    <td>{person.skills}</td>
                    <td>
                      <span className="resources-load">
                        <b
                          className={
                            person.load >= 100
                              ? "is-high"
                              : person.load >= 80
                              ? "is-medium"
                              : ""
                          }
                          style={{ width: `${Math.max(person.load, 10)}%` }}
                        >
                          {person.load}%
                        </b>
                      </span>
                    </td>
                    <td>
                      <span
                        className={`resources-status resources-status--${person.status
                          .toLowerCase()
                          .replace(/[^a-z]+/g, "-")}`}
                      >
                        {person.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!visible.length && (
              <p className="resources-empty">
                No reviewers match these filters.
              </p>
            )}
          </div>
          <footer>
            {selected.length
              ? `${selected.length} selected`
              : `Showing ${visible.length} of ${roster.length} reviewers`}
          </footer>
        </section>
        <section className="resources-panel resources-schedule">
          <header>
            <h2>Allocation Schedule</h2>
            <div>
              <button
                type="button"
                aria-label="Previous period"
                onClick={() =>
                  setOffset((value) => value - (period === "week" ? 7 : 28))
                }
              >
                <i className="bx bx-chevron-left" />
              </button>
              <strong>
                {dateText(slots[0])} – {dateText(slots[slots.length - 1])}
              </strong>
              <button
                type="button"
                aria-label="Next period"
                onClick={() =>
                  setOffset((value) => value + (period === "week" ? 7 : 28))
                }
              >
                <i className="bx bx-chevron-right" />
              </button>
              <div className="resources-period">
                <button
                  type="button"
                  className={period === "week" ? "is-active" : ""}
                  onClick={() => setPeriod("week")}
                >
                  Week
                </button>
                <button
                  type="button"
                  className={period === "month" ? "is-active" : ""}
                  onClick={() => setPeriod("month")}
                >
                  Month
                </button>
              </div>
              <button type="button" onClick={() => setOffset(0)}>
                Today
              </button>
            </div>
          </header>
          <div className="resources-schedule__scroll">
            <div
              className="resources-schedule__grid"
              style={{ "--schedule-days": days }}
            >
              <div className="resources-schedule__heading">Name</div>
              <div className="resources-schedule__heading">Role</div>
              {slots.map((date) => (
                <div
                  className="resources-schedule__date"
                  key={date.toISOString()}
                >
                  <small>
                    {date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </small>
                  <b>{date.getDate()}</b>
                  <span>
                    {date.toLocaleDateString("en-US", { weekday: "short" })}
                  </span>
                </div>
              ))}
              {visible
                .filter((person) => person.startDate && person.endDate)
                .map((person) => {
                  const start =
                    (new Date(person.startDate) - baseDate) / dayMs - offset;
                  const end =
                    (new Date(person.endDate) - baseDate) / dayMs - offset;
                  const left = Math.max(0, start);
                  const width = Math.max(0, Math.min(days, end) - left);
                  return (
                    <React.Fragment key={person.id}>
                      <div className="resources-schedule__name">
                        {person.name}
                      </div>
                      <div className="resources-schedule__role">
                        {person.role}
                      </div>
                      <div
                        className="resources-schedule__track"
                        style={{ gridColumn: `3 / span ${days}` }}
                      >
                        <span
                          className="resources-schedule__bar is-blue"
                          style={{
                            left: `${(left / days) * 100}%`,
                            width: `${(width / days) * 100}%`,
                            display: width ? undefined : "none",
                          }}
                        >
                          {person.engagement} – {person.inspection}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        </section>
      </div>
      <div className="resources-bottom">
        <section className="resources-panel resources-changes">
          <header>
            <h2>
              <i className="bx bx-time-five" /> Recent Allocation Changes
            </h2>
          </header>
          <div className="resources-small-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reviewer</th>
                  <th>Action</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {(data.changes || []).map((row, index) => (
                  <tr key={`${row.date}-${index}`}>
                    <td>{dateText(row.date)}</td>
                    <td>{row.reviewer || "System"}</td>
                    <td>{row.action}</td>
                    <td>{row.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="resources-panel resources-skills">
          <header>
            <h2>
              <i className="bx bx-pie-chart-alt-2" /> Workload Distribution
            </h2>
          </header>
          <div className="resources-skills__chart">
            {(data.workload || []).map((row, index, rows) => {
              const max = Math.max(1, ...rows.map((item) => item.value));
              return (
                <div key={row.label}>
                  <b>{row.value}</b>
                  <span
                    className={`is-${
                      ["blue", "green", "purple", "amber", "slate"][index % 5]
                    }`}
                    style={{ height: `${(row.value / max) * 100}%` }}
                  />
                  <small>{row.label}</small>
                </div>
              );
            })}
          </div>
        </section>
        <section className="resources-panel resources-attention">
          <header>
            <h2>
              <i className="bx bx-error" /> Items Requiring Attention
            </h2>
          </header>
          <div className="resources-small-table">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Reviewer / Inspection</th>
                  <th>Issue</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {(data.attention || []).map((row, index) => (
                  <tr key={`${row.subject}-${index}`}>
                    <td>
                      <i
                        className={`bx ${
                          row.type === "critical"
                            ? "bx-error-circle"
                            : "bx-error"
                        } is-${row.type}`}
                      />
                    </td>
                    <td>{row.subject}</td>
                    <td>{row.issue}</td>
                    <td>{dateText(row.dueDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
